import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard, Plus } from 'lucide-react';
import {
  fetchAgents,
  selectAgents,
  selectAgentsStatus,
} from '@/redux/agentsSlice';
import { subscriptionPlansApi } from '@/services/api';
import type { AppDispatch } from '@/redux/store';
import { AgentSubscriptionCard } from '@/components/page-components/billing/AgentSubscriptionCard';
import { sortAgentsBySubscription } from '@/utils/subscriptionUtils';
import { useToast } from '@/hooks/useToast';

const Billing: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const agents = useSelector(selectAgents);
  const agentsStatus = useSelector(selectAgentsStatus);

  const [cancellingAgentId, setCancellingAgentId] = useState<number | null>(
    null
  );

  const refreshAgents = useCallback(async () => {
    await dispatch(fetchAgents());
  }, [dispatch]);

  useEffect(() => {
    if (agentsStatus === 'idle') {
      void dispatch(fetchAgents());
    }
  }, [dispatch, agentsStatus]);

  const handleCancelRequest = async (agentId: number) => {
    setCancellingAgentId(agentId);
    try {
      await subscriptionPlansApi.cancelSubscription(agentId);
      toast({
        title: t(
          'subscription.cancel_success.title',
          'Subscription Request Cancelled'
        ),
        description: t(
          'subscription.cancel_success.description',
          'Your subscription request has been cancelled successfully.'
        ),
      });
      await refreshAgents();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('common.unknown_error.translation', 'Unknown error');
      toast({
        variant: 'destructive',
        title: t('subscription.cancel_error.title', 'Cancellation Failed'),
        description: t('subscription.cancel_error.description', {
          error: errorMessage,
          defaultValue: '{{error}}',
        }),
      });
    } finally {
      setCancellingAgentId(null);
    }
  };

  const isLoading = agentsStatus === 'loading' || agentsStatus === 'idle';
  const sortedAgents = sortAgentsBySubscription(agents);

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 sm:gap-8">
        <div className="flex flex-col gap-2">
          <Label className="text-2xl font-semibold sm:text-3xl">
            {t('sidebar.billing.translation', 'Billing')}
          </Label>
          <Label className="text-sm text-muted-foreground">
            {t(
              'billing.description.translation',
              'Manage subscriptions and usage for each agent.'
            )}
          </Label>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full rounded-lg" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </div>
        ) : agentsStatus === 'failed' ? (
          <Card className="bg-card">
            <CardContent className="flex flex-col items-center gap-4 py-8">
              <p className="text-sm text-muted-foreground">
                {t(
                  'billing.error.agents.translation',
                  'Failed to load agents.'
                )}
              </p>
              <Button variant="outline" onClick={() => void refreshAgents()}>
                {t('common.retry', 'Retry')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <section className="space-y-4">
              <h2 className="text-lg font-semibold">
                {t('billing.agents.title.translation', 'Agent subscriptions')}
              </h2>

              {sortedAgents.length === 0 ? (
                <Card className="bg-card">
                  <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'billing.agents.empty.translation',
                        'You have no agents yet. Create an agent to manage subscriptions.'
                      )}
                    </p>
                    <Button variant="brand" asChild>
                      <Link to="/agents/new">
                        <Plus className="mr-2 h-4 w-4" />
                        {t('sidebar.new_agent.translation', 'New Agent')}
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="flex flex-col gap-4">
                  {sortedAgents.map(agent => (
                    <AgentSubscriptionCard
                      key={agent.id}
                      agent={agent}
                      onRefresh={refreshAgents}
                      onCancelRequest={handleCancelRequest}
                      isCancelling={cancellingAgentId === agent.id}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {t('billing.payment.title.translation', 'Payment Method')}
              </CardTitle>
              <CardDescription>
                {t(
                  'billing.payment.description.translation',
                  'Manage your payment methods and billing information'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col space-y-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'billing.payment.empty.translation',
                      'No payment method on file'
                    )}
                  </p>
                  <Badge variant="gradient" className="text-primary-foreground">
                    {t('billing.coming_soon.translation', 'Coming soon')}
                  </Badge>
                </div>
                <Button variant="outline" className="w-full sm:w-auto" disabled>
                  {t('billing.payment.add.translation', 'Add Payment Method')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card">
            <CardHeader>
              <CardTitle>
                {t('billing.history.title.translation', 'Billing History')}
              </CardTitle>
              <CardDescription>
                {t(
                  'billing.history.description.translation',
                  'View your past invoices and payment history'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <p className="text-sm text-muted-foreground">
                  {t(
                    'billing.history.empty.translation',
                    'No billing history available'
                  )}
                </p>
                <Badge variant="gradient" className="text-primary-foreground">
                  {t('billing.coming_soon.translation', 'Coming soon')}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Billing;
